import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ScrollText, Loader2, Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/lib/login";

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, refreshEntitlement } = useAuth();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const next = location.state?.from || "/analyze";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success(data.message || "Reset link generated.");
        setTab("login");
        return;
      }
      const path = tab === "login" ? "/auth/login" : "/auth/register";
      const body = tab === "login" ? { email, password } : { name, email, password };
      const { data } = await api.post(path, body);
      setUser(data);
      refreshEntitlement();
      toast.success(tab === "login" ? `Welcome back, ${data.name}` : "Account created — 100 free reviews are on us");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain flex min-h-screen flex-col">
      <header className="border-b border-stone-200">
        <div className="mx-auto flex max-w-[1400px] items-center px-6 py-4 lg:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="login-logo">
            <span className="flex h-9 w-9 items-center justify-center bg-[#0f172a] text-white"><ScrollText className="h-4 w-4" /></span>
            <span className="font-display text-lg font-bold tracking-tight">Ledger<span className="text-slate-500">Check</span></span>
          </Link>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-[1100px] flex-1 items-center gap-16 px-6 py-16 lg:grid-cols-[1fr_420px] lg:px-12">
        <section className="hidden lg:block">
          <p className="overline text-slate-500">Sign in</p>
          <h1 className="font-display mt-4 text-4xl font-bold leading-tight tracking-tight">
            Your reviews,<br />saved and shareable.
          </h1>
          <ul className="mt-10 space-y-4 text-sm leading-relaxed text-slate-600">
            <li className="flex gap-3"><span className="mt-1.5 h-1 w-1 shrink-0 bg-[#0f172a]" />100 free entry reviews on every new account.</li>
            <li className="flex gap-3"><span className="mt-1.5 h-1 w-1 shrink-0 bg-[#0f172a]" />History of every verdict, with PDF export and share links.</li>
            <li className="flex gap-3"><span className="mt-1.5 h-1 w-1 shrink-0 bg-[#0f172a]" />Invoice PDF and day-book uploads, Ind AS vs AS comparison.</li>
          </ul>
        </section>

        <section className="border border-stone-200 bg-white p-8" data-testid="login-card">
          <div className="flex gap-6 border-b border-stone-200">
            {[["login", "Sign in"], ["register", "Create account"]].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`overline pb-3 transition-colors duration-150 ${tab === k ? "border-b-2 border-[#0f172a] text-[#0f172a]" : "border-b-2 border-transparent text-slate-400 hover:text-slate-600"}`}>
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {tab === "register" && (
              <label className="block">
                <span className="overline mb-2 block text-slate-500">Name</span>
                <div className="relative">
                  <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="CA Ramesh Iyer" data-testid="name-input"
                    className="rounded-none border-stone-300 pl-9 focus-visible:ring-stone-900" />
                </div>
              </label>
            )}

            <label className="block">
              <span className="overline mb-2 block text-slate-500">Email</span>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@firm.in" data-testid="email-input"
                  className="rounded-none border-stone-300 pl-9 focus-visible:ring-stone-900" />
              </div>
            </label>

            {tab !== "forgot" && (
              <label className="block">
                <span className="overline mb-2 block text-slate-500">Password</span>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" data-testid="password-input"
                    className="rounded-none border-stone-300 pl-9 focus-visible:ring-stone-900" />
                </div>
              </label>
            )}

            <button type="submit" disabled={busy} data-testid="submit-btn"
              className="flex w-full items-center justify-center gap-2 bg-[#0f172a] px-6 py-4 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800 disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Please wait…</>
                : tab === "login" ? <>Sign in <ArrowRight className="h-4 w-4" /></>
                : tab === "register" ? <>Create account <ArrowRight className="h-4 w-4" /></>
                : <>Send reset link</>}
            </button>
          </form>

          {tab === "login" && (
            <button onClick={() => setTab("forgot")} data-testid="forgot-btn"
              className="mt-4 font-mono-data text-xs text-slate-500 underline transition-colors duration-150 hover:text-[#0f172a]">
              forgot password?
            </button>
          )}
          {tab === "forgot" && (
            <button onClick={() => setTab("login")} data-testid="back-to-login-btn"
              className="mt-4 font-mono-data text-xs text-slate-500 underline transition-colors duration-150 hover:text-[#0f172a]">
              back to sign in
            </button>
          )}

          <div className="my-8 flex items-center gap-4">
            <span className="h-px flex-1 bg-stone-200" />
            <span className="overline text-slate-400">or</span>
            <span className="h-px flex-1 bg-stone-200" />
          </div>

          <button onClick={startGoogleLogin} data-testid="google-login-btn"
            className="flex w-full items-center justify-center gap-3 border border-stone-300 px-6 py-4 text-sm font-medium text-slate-700 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a]">
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.2-2.2H12v4.2h6.6c-.1 1.1-.8 2.8-2.4 3.9l-.1.1 3.5 2.7.2.1c2.2-2.1 3.7-5 3.7-8.8z" />
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.8-2.9l-3.7-2.9c-1 .7-2.3 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5l-.1.1-3.6 2.8-.1.1C3.4 21.3 7.4 24 12 24z" />
              <path fill="#FBBC05" d="M5.3 14.4c-.3-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4V9.5L1.6 6.7l-.1.1C.5 8.4 0 10.1 0 12s.5 3.6 1.5 5.2l3.8-2.8z" />
              <path fill="#EA4335" d="M12 4.7c2.2 0 3.7.9 4.6 1.8l3.3-3.2C17.9 1.4 15.2 0 12 0 7.4 0 3.4 2.7 1.5 6.7l3.8 2.9C6.2 6.8 8.9 4.7 12 4.7z" />
            </svg>
            Continue with Google
          </button>

          <p className="mt-8 font-mono-data text-[11px] leading-relaxed text-slate-400">
            Either method works on the same account. Sign up with Google today and you can add a password later.
          </p>
        </section>
      </main>
    </div>
  );
}
